import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, CircularProgress, Stack, Typography, useTheme } from "@mui/material";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FoodsIllustrations, WhiteBox } from "@foodpilot/foods";
import { Navigate } from "react-router-dom";
import { FoodsCheckbox, FoodsIcon } from "@foodpilot/foods";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import { useCreateBatchConsent } from "src/api/client-api/consent/useCreateBatchConsent";
import { Policy, useGetAllPolicy } from "src/api/client-api/policy/useGetAllPolicy";
import { z } from "zod";
import { URLHandler } from "src/services/URLHandler";
import { UserService } from "src/services/UserService";

const ConsentWriteSchema = z.object({
    policy: z.string(),
    version: z.string(),
    accepted: z.boolean(),
});

export type ConsentWrite = z.infer<typeof ConsentWriteSchema>;

type ConsentsWrite = {
    consents: ConsentWrite[];
};

export const Consents = () => {
    const theme = useTheme();
    const [error, setError] = useState<string | null>(null);
    const { t } = useTranslation();
    const { policies } = useGetAllPolicy();

    const consentsValues: ConsentWrite[] =
        policies?.map((policy: Policy): ConsentWrite => {
            return {
                policy: policy.name,
                version: policy.version,
                accepted: true,
            };
        }) ?? [];

    const methods = useForm<ConsentsWrite>({
        defaultValues: {
            consents: [],
        },
        resolver: zodResolver(
            z.object({
                consents: ConsentWriteSchema.array().nonempty({ message: t("terms.need-to-accept") }),
            }),
        ),
    });

    const { mutate: giveConsents, isLoading, isSuccess } = useCreateBatchConsent();

    const gcuPolicy = policies?.find((policy: Policy) => policy.name === "gcu");
    const gcuDocumentUrl = gcuPolicy?.url;

    const { control, handleSubmit } = methods;

    if (isSuccess || UserService.isImpersonationActive()) {
        return <Navigate to={URLHandler.home()} replace />;
    }

    return (
        <FormProvider {...methods}>
            <WhiteBox padding={theme.spacing(4)} boxShadow="0 2px 8px 0 rgba(42, 43, 39, 0.04)" position="relative">
                {isLoading && (
                    <Stack
                        sx={{
                            background: "rgba(255, 255, 255, 0.5)",
                            position: "absolute",
                            inset: theme.spacing(2.5),
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 1,
                        }}
                    >
                        <CircularProgress />
                    </Stack>
                )}
                <Stack
                    component="form"
                    onSubmit={handleSubmit(
                        (values) => {
                            methods.clearErrors();
                            setError(null);

                            giveConsents(values, {
                                onSuccess: (response) => {
                                    setError(null);
                                },
                                onError: (error) => {
                                    setError((error as Error).message);
                                },
                            });
                        },
                        (errors) => {
                            setError(errors.consents?.message ?? t("Une erreur s'est produite"));
                        },
                    )}
                    autoComplete="off"
                    gap={theme.spacing(4.5)}
                >
                    <Stack gap={theme.spacing(2)} alignItems="center">
                        <FoodsIllustrations illustration="consents" />

                        <Typography variant="h2" align="center">
                            {t("terms.title")}
                        </Typography>
                        <Typography variant="body" align="left" sx={{ color: theme.custom.grey[2000] }}>
                            {t("terms.body.start")}
                            <a
                                href={gcuDocumentUrl}
                                target="_blank"
                                rel="noreferrer"
                                style={{ color: theme.custom.grey[2000] }}
                            >
                                {t("terms.body.document")}
                            </a>
                            {t("terms.body.end")}
                        </Typography>
                    </Stack>

                    <Stack direction="column">
                        {error && (
                            <Typography variant="body-medium" align="center" color="error">
                                {error}
                            </Typography>
                        )}
                        <Stack direction="row" alignItems="center">
                            <Controller
                                name="consents"
                                control={control}
                                render={({ field }) => (
                                    <FoodsCheckbox
                                        selectedItems={field.value}
                                        values={consentsValues}
                                        onChange={(updatedConsents) => field.onChange(updatedConsents)}
                                        getName={() => t("terms.checkbox")}
                                        FormControlLabelProps={{
                                            sx: {
                                                gap: theme.spacing(0.5),
                                                ".MuiTypography-root": {
                                                    ...theme.typography.body,
                                                    fontWeight: 500,
                                                },
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Stack>
                    </Stack>

                    <Stack direction="column">
                        <Accordion
                            disableGutters
                            sx={{
                                background: theme.palette.primary[50],
                                borderRadius: theme.spacing(1),
                                boxShadow: "none",
                                padding: theme.spacing(2),
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<FoodsIcon icon="arrowDownShort" size={2} />}
                                aria-controls="panel1-content"
                                id="panel1-header"
                                sx={{
                                    "&>.MuiAccordionSummary-content": {
                                        margin: 0,
                                    },
                                    minHeight: 0,
                                    padding: 0,
                                }}
                            >
                                <Typography variant="h5">{t("terms.accordion-title")}</Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ padding: 0 }}>
                                <Typography variant="body">
                                    <ul>
                                        <li>
                                            <strong>{t("terms.excerpt.first-title")}</strong>{" "}
                                            <p>{t("terms.excerpt.first-content")}</p>
                                        </li>
                                        <li>
                                            <strong>{t("terms.excerpt.second-title")}</strong>{" "}
                                            <p>{t("terms.excerpt.second-content")}</p>
                                        </li>
                                        <li>
                                            <strong>{t("terms.excerpt.third-title")}</strong>{" "}
                                            <p>{t("terms.excerpt.third-content")}</p>
                                        </li>
                                        <li>
                                            <strong>{t("terms.excerpt.fourth-title")}</strong>{" "}
                                            <p>{t("terms.excerpt.fourth-content")}</p>
                                        </li>
                                    </ul>
                                </Typography>
                                <Typography variant="body">
                                    <strong>{t("terms.excerpt.need-more-details")}</strong>{" "}
                                    {t("terms.excerpt.consult-full-cgu")}
                                </Typography>
                            </AccordionDetails>
                        </Accordion>
                    </Stack>

                    <Stack alignItems="flex-end">
                        <Button variant="primary" type="submit" startIcon={<FoodsIcon icon="check" size={2} />}>
                            {t("terms.login.button-validate")}
                        </Button>
                    </Stack>
                </Stack>
            </WhiteBox>
        </FormProvider>
    );
};
